// Libs
import classNames from 'classnames/bind';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useContext, useEffect, useState } from 'react';
import { Box, Button, Grid2, Stack, Typography } from '@mui/material';
// Components, Layouts, Pages
import { BaseAvatar, Loading } from '~/components/common';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getDetailClient } from '~/thunks/client/clientThunk';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { EMPTY_STRING, EMPTY_VALUE } from '~/utils/constants/common';
import {
  BusinessTypeEnum,
  ConnectionStatusEnum,
  RoleEnum,
  TypeCrmEnum,
  TypeDetailClientEnum,
} from '~/utils/enum/common';
import { formatPhoneNumber, getFullName } from '~/utils/helper/common';
import { RingCentralContext } from '~/context';
import { IClientOverview, IParamDetailClient } from '~/utils/interface/client';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { usePermissions } from '~/utils/hooks/userPermissions';
import { CLIENT_PERMISSIONS, LEAD_PERMISSIONS } from '~/utils/constants/permissions';
// Styles, images, icons
import styles from './InfoClientDetailTab.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const InfoClientDetailTab = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canEditLeads } = usePermissions(LEAD_PERMISSIONS);
  const { canEditClients } = usePermissions(CLIENT_PERMISSIONS);
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/client', 'common/base']);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { id: clientId } = useParams();
  const [searchParams] = useSearchParams();
  const typeList = searchParams.get('type');
  const { ringCentralStatus, executeCall } = useContext(RingCentralContext);
  const currentRole = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0];
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [detailClient, setDetailClient] = useState<IClientOverview>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (!clientId) return;

    const params: IParamDetailClient = {
      clientId: clientId,
      type: TypeDetailClientEnum.OVERVIEW,
    };

    handleGetDetailClient(params);
  }, [clientId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetDetailClient = (params: IParamDetailClient) => {
    setIsLoading(true);

    dispatch(getDetailClient(params))
      .unwrap()
      .then((res) => {
        if (!res?.data) return;

        setDetailClient(res.data as IClientOverview);
      })
      .catch((_error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const renderHeader = (title: string, icon?: React.ReactElement) => {
    return (
      <Stack direction={'row'} gap={2}>
        <>{icon}</>

        <Typography className={cx('title')}>{title}</Typography>
      </Stack>
    );
  };

  const renderItemValue = (title: string, value: string, icon?: React.ReactElement, handleClick?: () => void) => {
    return (
      <Stack gap={2}>
        <Stack gap={2} direction={'row'}>
          {icon && <Box className={cx('icon')}>{icon}</Box>}

          <Typography className={cx('label')}>{title}</Typography>
        </Stack>

        <Typography className={cx('value', { textUnderline: Boolean(handleClick) })} onClick={handleClick}>
          {value}
        </Typography>
      </Stack>
    );
  };

  const handleEdit = () => {
    if (!clientId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? adminRouteAbsolute.updateClient(clientId)
              : adminRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesAdminRouteAbsolute.updateClient(clientId)
              : salesAdminRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.SALES_PERSON:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? salesPersonRouteAbsolute.updateClient(clientId)
              : salesPersonRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? operationsManagerRouteAbsolute.updateClient(clientId)
              : operationsManagerRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseLeadRouteAbsolute.updateClient(clientId)
              : warehouseLeadRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? warehouseStaffRouteAbsolute.updateClient(clientId)
              : warehouseStaffRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.FOREMAN:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? foremanRouteAbsolute.updateClient(clientId)
              : foremanRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.CREW:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? crewRouteAbsolute.updateClient(clientId)
              : crewRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
      case RoleEnum.LOADER:
        navigate(
          `${
            typeList === TypeCrmEnum.CLIENT
              ? loaderRouteAbsolute.updateClient(clientId)
              : loaderRouteAbsolute.updateLead(clientId)
          }?type=${typeList}`
        );
        break;
    }
  };

  const handleClickPhoneNumber = (phoneNumber?: string) => {
    if (ringCentralStatus !== ConnectionStatusEnum.CONNECTED) {
      alert(t('Ringcentral is not connected'));
      return;
    }

    if (phoneNumber && clientId) {
      executeCall && executeCall(phoneNumber, clientId, BusinessTypeEnum.CLIENT);
    }
  };
  //#endregion Handle Function

  return (
    <Stack id='infoClientDetailTabComponent' className={cx('container')}>
      <Stack className={cx('viewHeader')}>
        <Stack direction={'row'} gap={3} alignItems={'center'}>
          <BaseAvatar
            dataAccount={{
              firstName: detailClient?.firstName || EMPTY_STRING,
              lastName: detailClient?.lastName || EMPTY_STRING,
              avatarImage: detailClient?.avatarImage || EMPTY_STRING,
            }}
            size={65}
            fontSize={24}
          />

          <Typography className={cx('textName')}>
            {getFullName({
              firstName: detailClient?.firstName || EMPTY_STRING,
              lastName: detailClient?.lastName || EMPTY_STRING,
            })}
          </Typography>
        </Stack>

        {(typeList === TypeCrmEnum.CLIENT ? canEditClients : canEditLeads) && (
          <Button
            type='button'
            variant='contained'
            className={cx('buttonEdit')}
            onClick={handleEdit}
            startIcon={<img src={icons.commonIconPen} alt={'common_alt_img'} />}
          >
            {t('client_detail_title_edit_details')}
          </Button>
        )}
      </Stack>

      <Grid2 container spacing={8} padding={7}>
        <Grid2 size={6}>
          <Grid2 container spacing={10}>
            <Grid2 size={7}>
              <Stack gap={'15px'}>
                <>
                  {renderHeader(
                    t('client_detail_title_personal_information'),
                    <img src={icons.commonIconProfile} alt={t('common_img_text_alt')} />
                  )}

                  {renderItemValue(
                    t('client_detail_title_first_name'),
                    detailClient?.firstName || EMPTY_VALUE,
                    <img src={icons.commonIconProfileSmall} alt={t('common_img_text_alt')} />
                  )}

                  {renderItemValue(
                    t('client_detail_title_last_name'),
                    detailClient?.lastName || EMPTY_VALUE,
                    <img src={icons.commonIconProfileSmall} alt={t('common_img_text_alt')} />
                  )}

                  {renderItemValue(
                    t('client_detail_title_company'),
                    detailClient?.companyName || EMPTY_VALUE,
                    <img src={icons.commonIconCompany} alt={t('common_img_text_alt')} />
                  )}

                  {renderItemValue(
                    t('client_detail_title_title'),
                    detailClient?.title || EMPTY_VALUE,
                    <img src={icons.commonIconJob} alt={t('common_img_text_alt')} />
                  )}
                </>

                <>
                  {renderHeader(
                    t('client_detail_title_contact_information'),
                    <img src={icons.commonIconPhoneNumber} alt={t('common_img_text_alt')} />
                  )}

                  {detailClient?.clientContacts &&
                    detailClient?.clientContacts.map((item, index) => (
                      <Stack gap={'15px'} key={index}>
                        {renderItemValue(
                          t('client_detail_title_phone'),
                          formatPhoneNumber(item.phoneNumber) || EMPTY_VALUE,
                          <img src={icons.commonIconPhoneNumberSmall} alt={t('common_img_text_alt')} />,
                          () => handleClickPhoneNumber(item.phoneNumber)
                        )}

                        {renderItemValue(
                          t('client_detail_title_email'),
                          item.email || EMPTY_VALUE,
                          <img src={icons.commonIconMall} alt={t('common_img_text_alt')} />
                        )}
                      </Stack>
                    ))}
                </>
              </Stack>
            </Grid2>

            <Grid2 size={5}>
              <Stack gap={'15px'}>
                {renderHeader(
                  t('client_detail_title_address'),
                  <img src={icons.commonIconLocation} alt={t('common_img_text_alt')} />
                )}

                {renderItemValue(
                  t('client_detail_title_street_address'),
                  detailClient?.address?.address || EMPTY_VALUE,
                  <img src={icons.commonIconHome} alt={t('common_img_text_alt')} />
                )}

                {renderItemValue(
                  t('client_detail_title_suite_apt'),
                  detailClient?.address?.unitDetail || EMPTY_VALUE,
                  <img src={icons.commonIconSuite} alt={t('common_img_text_alt')} />
                )}

                {renderItemValue(t('client_detail_title_city'), detailClient?.address?.city || EMPTY_VALUE)}

                {renderItemValue(t('client_detail_title_state'), detailClient?.address?.state || EMPTY_VALUE)}

                {renderItemValue(t('client_detail_title_country'), detailClient?.address?.country || EMPTY_VALUE)}

                {renderItemValue(
                  t('client_detail_title_salesperson_assigned'),
                  getFullName({
                    firstName: detailClient?.salesUser?.firstName || EMPTY_STRING,
                    lastName: detailClient?.salesUser?.lastName || EMPTY_STRING,
                  }) || EMPTY_VALUE
                )}
              </Stack>
            </Grid2>
          </Grid2>
        </Grid2>

        <Grid2 size={6}>
          <Stack gap={4}>
            {renderHeader(t('client_detail_title_additional_notes'))}

            <Box className={cx('viewBox')}>{detailClient?.note || EMPTY_VALUE}</Box>
          </Stack>
        </Grid2>
      </Grid2>

      <Stack className={cx('viewFooter')} gap={8} direction={'row'}>
        <Stack>
          <Typography className={cx('titleFooter')}>{t('client_detail_title_created')}</Typography>
          <Typography className={cx('valueFooter')}>{detailClient?.createdAt || EMPTY_VALUE}</Typography>
        </Stack>

        <Stack>
          <Typography className={cx('titleFooter')}>{t('client_detail_title_last_contact')}</Typography>
          <Typography className={cx('valueFooter')}>{detailClient?.lastContact || EMPTY_VALUE}</Typography>
        </Stack>
      </Stack>

      {isLoading && <Loading />}
    </Stack>
  );
};

export default InfoClientDetailTab;
